/**
 * Real `AgentAPI` implementation backed by the HTTP client (ADR-0019 §3.6).
 *
 * Two methods: `list` (GET /v1/agents) and `get` (client-side lookup
 * over the cached list). Agents is a global registry — every account
 * sees the same rows (ADR-0010 §3.11). The list is small (~3 entries
 * in Phase 1: Anthropic, OpenAI, Google) and changes only with
 * seed-migration deploys.
 *
 * Per-bag in-memory cache: safe for the page-load lifetime. The cache
 * invalidates on page refresh. A future ADR can add a refresh-interval
 * if seed updates become more frequent; Phase 1 ships with no
 * invalidation.
 *
 * ADR-0021 aligned the dashboard `Agent` type with the server wire
 * shape, so the response type is `{ items: Agent[] }` directly — no
 * adapter needed. (Pre-ADR-0021 the two shapes diverged and a
 * placeholder-filling `wireToAgent` adapter shimmed the gap.)
 */

import type { Agent, AgentId } from '@sill/types';
import type { AgentAPI } from '@sill/types/api';

import { ApiError, type HttpClient } from './http-client.js';

export interface RealAgentsDeps {
  readonly http: HttpClient;
}

interface AgentsListResponse {
  items: Agent[];
}

export function createRealAgentsApi(deps: RealAgentsDeps): AgentAPI {
  let cache: Agent[] | null = null;

  async function fetchList(): Promise<Agent[]> {
    if (cache !== null) return cache;
    const res = await deps.http.request<AgentsListResponse>({
      method: 'GET',
      path: '/v1/agents',
    });
    if (res === undefined) {
      throw new ApiError('bad_response', null);
    }
    cache = res.items;
    return cache;
  }

  return {
    list: fetchList,
    async get(id: AgentId): Promise<Agent | null> {
      const items = await fetchList();
      return items.find((a) => a.agent_id === id) ?? null;
    },
  };
}
